import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Alert, Button, Input } from '@easydent/shared-ui';
import { topUpCard } from '@/lib/api';

/** The card form — mounted only once we have a PaymentIntent client secret. */
const CardForm: React.FC<{ amount: number; currency: string; onPaid: () => void }> = ({ amount, currency, onPaid }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setError(null);
    const { error: err, paymentIntent } = await stripe.confirmPayment({ elements, redirect: 'if_required' });
    if (err) { setError(err.message ?? t('wallet.payFailed', 'Payment could not be completed.')); setBusy(false); return; }
    if (paymentIntent?.status === 'succeeded') { onPaid(); return; }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="mt-3 text-left">
      <PaymentElement />
      {error && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
      <Button type="submit" className="mt-4 w-full" loading={busy} disabled={!stripe}>
        Pay {new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount)}
      </Button>
      <p className="mt-2 text-center text-xs text-subtle-ink">{t('topUp.secured', "Secured by Stripe. Your balance updates once the payment clears.")}</p>
    </form>
  );
};

/**
 * Card wallet top-up (Stripe). Config-gated: when Stripe isn't configured the intent call 422s and we
 * surface a friendly note (the manual "Add funds" path still works). Credited by the webhook on success.
 */
const CardTopUp: React.FC<{ currency: string; onClose: () => void; onPaid: () => void }> = ({ currency, onClose, onPaid }) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('50');
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  const amountNum = Number(amount);
  const valid = Number.isFinite(amountNum) && amountNum > 0 && amountNum <= 100000;

  const begin = async () => {
    if (!valid) return;
    setStarting(true); setError(null);
    try {
      const { clientSecret: secret, publishableKey } = await topUpCard(amountNum);
      if (!publishableKey) throw new Error('no-key');
      setStripePromise(loadStripe(publishableKey));
      setClientSecret(secret);
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(status === 422
        ? t('wallet.cardsOffManual', 'Card payments aren’t configured yet. Use “Add funds” for a manual top-up.')
        : t('wallet.cardStartFailed', 'Could not start the card payment. Please try again.'));
    } finally { setStarting(false); }
  };

  const options = useMemo(
    () => (clientSecret ? { clientSecret, appearance: { theme: 'stripe' as const } } : undefined),
    [clientSecret],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-surface p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-body">{t('topUp.title', "Pay by card")}</h2>
          <button onClick={onClose} className="text-subtle-ink hover:text-muted-strong" aria-label={t('common.close', "Close")}>✕</button>
        </div>

        {error && <Alert variant="error" className="mt-3">{error}</Alert>}

        {!clientSecret ? (
          <div className="mt-4 space-y-3">
            <label className="block text-sm text-muted-strong">Amount ({currency})</label>
            <Input type="number" min="1" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <Button className="w-full" loading={starting} disabled={!valid} onClick={begin}>{t('common.continue', "Continue")}</Button>
          </div>
        ) : (
          stripePromise && options && (
            <Elements stripe={stripePromise} options={options}>
              <CardForm amount={amountNum} currency={currency} onPaid={onPaid} />
            </Elements>
          )
        )}
      </div>
    </div>
  );
};

export default CardTopUp;
